import { UseQueryResult } from '@tanstack/react-query'
import { IntlShape } from 'react-intl'
import { LIMITS } from 'shared'
import { create, enforce, omitWhen, test } from 'vest'
import { messages } from '../messages'
import { UtmsType } from './CreateCoupon'
import { CouponForm } from './Editor'
import { UseCouponsReturn } from './hooks/useCouponQuery'

const MIN_VALUE = 0

const NAME_REGEX = /^[a-zA-Z0-9-]+$/

// The reason is that the name 'new' is reserved word for the creation of new coupons (route)
const BLOCKED_NAMES = ['new']

export function validationSuite(
  intl: IntlShape,
  utms: UtmsType,
  couponQuery: UseQueryResult<UseCouponsReturn, Error>
) {
  return create((config: CouponForm) => {
    const { $t } = intl
    const { validation } = messages
    const { name, limit, group, utmSource, utmCampaign } = config

    omitWhen(!limit.checked, () => {
      test(
        'limit.value',
        $t(validation.greaterThan, { value: MIN_VALUE }),
        () => {
          enforce(limit.value).gt(MIN_VALUE)
        }
      )
    })

    omitWhen(!group.checked, () => {
      test(
        'group.value',
        $t(validation.greaterThan, { value: MIN_VALUE }),
        () => {
          enforce(group.value).gt(0)
        }
      )

      test(
        'group.value',
        $t(validation.lessThanEquals, { value: LIMITS.MAX_COUPONS_GROUP }),
        () => {
          enforce(group.value).lte(LIMITS.MAX_COUPONS_GROUP)
        }
      )
    })

    test('name', $t(messages.validation.emptyName), () => {
      enforce(name.length).gt(0)
    })

    test(
      'name',
      $t(messages.validation.lessThanEquals, { value: LIMITS.MAX_COUPON_NAME }),
      () => {
        enforce(name.length).lte(LIMITS.MAX_COUPON_NAME)
      }
    )

    test('name', $t(messages.validation.specialChars), () => {
      enforce(name).matches(NAME_REGEX)
    })

    test('name', $t(messages.validation.notAllowed), () => {
      if (BLOCKED_NAMES.includes(name)) {
        throw new Error()
      }
    })

    test('name', $t(messages.validation.unique), async () => {
      const { refetch, isFetched, isError } = couponQuery

      if (isFetched) {
        enforce(isError).equals(true)
      } else {
        const { isError } = await refetch()
        enforce(isError).equals(true)
      }
    })

    omitWhen(!utms.utmSource.length, () => {
      test('utmSource', $t(messages.validation.utmSource), () => {
        enforce(utmSource.length).gt(0)
      })
    })

    omitWhen(!utms.utmCampaign.length, () => {
      test('utmCampaign', $t(messages.validation.utmCampaign), () => {
        enforce(utmCampaign.length).gt(0)
      })
    })
  })
}
